
// ============================================================
// Notifications WhatsApp via CallMeBot API (gratuit, sans serveur)
// L'utilisateur doit d'abord activer CallMeBot sur son numéro :
// Envoyer "I allow callmebot to send me messages" au +34 644 597 91 sur WhatsApp
// ============================================================

/**
 * Envoie un message WhatsApp à un numéro via CallMeBot
 * @param phone - numéro international sans espaces ni + (ex: 22892252664)
 * @param message - texte du message
 */
export const sendWhatsAppNotification = async (
  phone: string,
  message: string
): Promise<boolean> => {
  try {
    // Nettoyer le numéro : enlever +, espaces, tirets
    const cleanPhone = phone.replace(/[\s+\-()]/g, '');
    const encodedMessage = encodeURIComponent(message);

    // Récupérer la clé API CallMeBot stockée pour ce numéro
    const apiKey = localStorage.getItem(`callmebot_key_${cleanPhone}`) || '1234567';

    const url = `https://api.callmebot.com/whatsapp.php?phone=${cleanPhone}&text=${encodedMessage}&apikey=${apiKey}`;

    await fetch(url, { method: 'GET', mode: 'no-cors' });
    return true;
  } catch (error) {
    console.error('Erreur notification WhatsApp:', error);
    return false;
  }
};

/**
 * Notification : compte activé avec succès
 */
export const notifyAccountActivated = async (
  phone: string,
  userName: string,
  lang: 'fr' | 'en' = 'fr'
): Promise<void> => {
  const message = lang === 'en'
    ? `✅ Hello ${userName}! Your SUCCESSLINK AGENCY account has been successfully activated. You can now log in and access your dashboard. Welcome! 🎉`
    : `✅ Bonjour ${userName} ! Votre compte SUCCESSLINK AGENCY a été activé avec succès. Vous pouvez maintenant vous connecter et accéder à votre tableau de bord. Bienvenue ! 🎉`;

  await sendWhatsAppNotification(phone, message);
};

/**
 * Notification : dépôt validé par l'admin
 */
export const notifyDepositValidated = async (
  phone: string,
  userName: string,
  amount: number,
  currency: string,
  lang: 'fr' | 'en' = 'fr'
): Promise<void> => {
  const message = lang === 'en'
    ? `✅ Hello ${userName}! Your deposit of ${amount} ${currency} has been validated by the administrator. Your SUCCESSLINK AGENCY account is now active. Welcome! 🎉`
    : `✅ Bonjour ${userName} ! Votre dépôt de ${amount} ${currency} a été validé par l'administrateur. Votre compte SUCCESSLINK AGENCY est maintenant actif. Bienvenue ! 🎉`;

  await sendWhatsAppNotification(phone, message);
};

/**
 * Notification : retrait validé par l'admin
 */
export const notifyWithdrawalValidated = async (
  phone: string,
  userName: string,
  amount: number,
  currency: string,
  lang: 'fr' | 'en' = 'fr'
): Promise<void> => {
  const message = lang === 'en'
    ? `💸 Hello ${userName}! Your withdrawal of ${amount} ${currency} has been validated. The funds will be transferred to your account shortly. Thank you for trusting SUCCESSLINK AGENCY!`
    : `💸 Bonjour ${userName} ! Votre retrait de ${amount} ${currency} a été validé. Les fonds seront transférés sur votre compte sous peu. Merci de faire confiance à SUCCESSLINK AGENCY !`;

  await sendWhatsAppNotification(phone, message);
};

/**
 * Notification : retrait rejeté par l'admin
 */
export const notifyWithdrawalRejected = async (
  phone: string,
  userName: string,
  amount: number,
  currency: string,
  lang: 'fr' | 'en' = 'fr'
): Promise<void> => {
  const message = lang === 'en'
    ? `❌ Hello ${userName}. Your withdrawal request of ${amount} ${currency} has been rejected. Please contact support for more information.`
    : `❌ Bonjour ${userName}. Votre demande de retrait de ${amount} ${currency} a été rejetée. Veuillez contacter le support pour plus d'informations.`;

  await sendWhatsAppNotification(phone, message);
};

/**
 * Notification : dépôt rejeté par l'admin
 */
export const notifyDepositRejected = async (
  phone: string,
  userName: string,
  amount: number,
  currency: string,
  lang: 'fr' | 'en' = 'fr'
): Promise<void> => {
  const message = lang === 'en'
    ? `❌ Hello ${userName}. Your deposit of ${amount} ${currency} has been rejected. Please contact support for more information.`
    : `❌ Bonjour ${userName}. Votre dépôt de ${amount} ${currency} a été rejeté. Veuillez contacter le support pour plus d'informations.`;

  await sendWhatsAppNotification(phone, message);
};

/**
 * Notification : un filleul a activé son compte (envoyée au parrain)
 */
export const notifyReferrerFilleulActivated = async (
  referrerPhone: string,
  referrerName: string,
  filleulName: string,
  commission: number,
  currency: string,
  level: number,
  lang: 'fr' | 'en' = 'fr'
): Promise<void> => {
  const message = lang === 'en'
    ? `🎉 Great news ${referrerName}! Your referral ${filleulName} (Level ${level}) just activated their SUCCESSLINK AGENCY account. You earned +${commission} ${currency} commission! Check your dashboard to see your earnings. 💰`
    : `🎉 Bonne nouvelle ${referrerName} ! Votre filleul ${filleulName} (Niveau ${level}) vient d'activer son compte SUCCESSLINK AGENCY. Vous avez gagné +${commission} ${currency} de commission ! Consultez votre tableau de bord pour voir vos gains. 💰`;

  await sendWhatsAppNotification(referrerPhone, message);
};
